import { DURATION_OPTIONS } from "@/lib/constants";
import type {
  Meta,
  VoucherDetailsErrors,
  VoucherDetailsFields,
} from "@/lib/types";
import { validateMeta } from "@/lib/utils";
import {
  areConditionalsComplete,
  getVisibleQuestions,
  isRespondComplete,
  RESPOND_QUESTIONS,
} from "@/lib/vouch-flow";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSessionStorage } from "./use-session-storage";

// Stored values can contain submit-time backfills ("forever", "not-applicable")
// that aren't selectable options — drop them so they never appear as answers.
const sanitizeOption = (
  options: { value: string }[],
  value?: string,
): string =>
  options.some((option) => option.value === value) ? (value as string) : "";

export const useSubmitVouch = () => {
  const navigate = useNavigate();
  const { vouchId } = useParams<{ vouchId: string }>();

  const { getSessionStore, setSessionStore } = useSessionStorage();

  type StoredShape = Partial<Meta> & { Meta?: Partial<Meta> };

  const getBaseData = (): Partial<Meta> => {
    const stored = getSessionStore<StoredShape>(vouchId!, {});
    return stored.Meta ?? stored;
  };

  const [meta, setMeta] = useState(() => {
    const data = getBaseData();
    return {
      Relationship: data.Relationship ?? "",
      Duration: sanitizeOption(DURATION_OPTIONS, data.Duration),
      Comments: data.Comments ?? "",
    };
  });
  const [fieldError, setFieldError] = useState<VoucherDetailsErrors>({});

  const clearFieldError = (field: keyof VoucherDetailsFields) =>
    setFieldError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const updateMeta = (patch: Partial<typeof meta>) => {
    if (patch.Comments !== undefined) clearFieldError("Comments");
    setMeta((prev) => ({ ...prev, ...patch }));
  };

  const handleContinue = () => {
    const baseData = getBaseData();
    if (baseData.Accuracy === undefined || baseData.Endorsement === undefined)
      return;

    const errors = validateMeta({ Comments: meta.Comments });
    if (errors.Comments) {
      setFieldError(errors);
      return;
    }

    // Store the raw relationship answers; Duration/MaritalStatus are resolved to
    // their submit values on the "about you" screen (the last step before confirm).
    const mergedMeta: Partial<Meta> = {
      ...(baseData as Meta),
      Relationship: meta.Relationship,
      Duration: meta.Duration,
      Comments: meta.Comments,
    };

    setSessionStore(vouchId!, JSON.stringify(mergedMeta));
    navigate("../about-you");
  };

  return {
    relationship: meta.Relationship,
    duration: meta.Duration,
    comments: meta.Comments,
    fieldError,
    visibleQuestions: getVisibleQuestions(
      meta.Relationship,
      meta,
      RESPOND_QUESTIONS,
    ),
    showComments: areConditionalsComplete(
      meta.Relationship,
      meta,
      RESPOND_QUESTIONS,
    ),
    updateMeta,
    handleContinue,
    isDisabled: !isRespondComplete(meta.Relationship, meta),
  };
};
